import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileCode, CheckCircle, AlertCircle, Copy, Trash2, Download, FileText } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

const JsonFormatter: React.FC = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [error, setError] = useState('');
  const [indentSize, setIndentSize] = useState(2);

  useSEO({
    title: 'JSON Formatter & Validator - Format, Validate & Beautify JSON Online',
    description: 'Free online JSON formatter and validator. Format, beautify, validate JSON data with syntax highlighting. Fix JSON errors and minify JSON files.',
    keywords: 'JSON formatter, JSON validator, JSON beautifier, format JSON, validate JSON, JSON syntax checker, JSON minifier, JSON pretty print',
    canonical: 'https://snap-tools.online/dev/json-formatter'
  });

  useEffect(() => {
    if (!input.trim()) {
      setOutput('');
      setIsValid(null);
      setError('');
      return;
    }

    try {
      const parsed = JSON.parse(input);
      const formatted = JSON.stringify(parsed, null, indentSize);
      setOutput(formatted);
      setIsValid(true);
      setError('');
    } catch (err) {
      setIsValid(false);
      setError(err instanceof Error ? err.message : 'Invalid JSON');
      setOutput('');
    }
  }, [input, indentSize]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output).then(() => {
      toast.success('Formatted JSON copied to clipboard');
    });
  };

  const downloadJson = () => {
    const blob = new Blob([output], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'formatted.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('JSON file downloaded');
  };

  const clearInputs = () => {
    setInput('');
    setOutput('');
    toast.success('All fields cleared');
  };

  const minifyJson = () => {
    if (isValid && input.trim()) {
      try {
        const parsed = JSON.parse(input);
        const minified = JSON.stringify(parsed);
        setOutput(minified);
        toast.success('JSON minified');
      } catch (err) {
        toast.error('Cannot minify invalid JSON');
      }
    }
  };

  const loadSample = () => {
    const sample = {
      "name": "John Doe",
      "age": 30,
      "email": "john.doe@example.com",
      "address": {
        "street": "123 Main St",
        "city": "New York",
        "zipCode": "10001"
      },
      "hobbies": ["reading", "swimming", "coding"],
      "isActive": true,
      "lastLogin": "2024-01-15T10:30:00Z"
    };
    setInput(JSON.stringify(sample));
    toast.success('Sample JSON loaded');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-xl sm:rounded-2xl mb-4 sm:mb-6 shadow-lg">
            <FileCode className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8438ff] mb-3 sm:mb-4">
            JSON Formatter & Validator
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Format, validate, and beautify JSON data with professional-grade tools. Fix syntax errors, 
            customize indentation, and convert between formatted and minified JSON.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
          className="mb-6 sm:mb-8"
        />

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Input Section */}
          <div>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-5 sm:p-6 lg:p-8 hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-[#8438ff]" />
                  Input JSON
                </h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={loadSample}
                    className="px-3 py-1.5 text-xs sm:text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-200 font-medium shadow-sm"
                  >
                    Load Sample
                  </button>
                  {input && (
                    <button
                      onClick={clearInputs}
                      className="p-1.5 sm:p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200"
                      title="Clear all"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Paste your JSON here..."
                className={`w-full h-64 sm:h-80 lg:h-96 p-3 sm:p-4 border-2 rounded-lg sm:rounded-xl resize-none font-mono text-xs sm:text-sm transition-all duration-200 ${
                  isValid === false 
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50' 
                    : 'border-gray-200 focus:ring-[#8438ff] focus:border-[#8438ff] bg-white'
                }`}
              />

              {/* Controls */}
              <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:gap-4 items-start sm:items-center">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Indent:</label>
                  <select
                    value={indentSize}
                    onChange={(e) => setIndentSize(Number(e.target.value))}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff] bg-white"
                  >
                    <option value={2}>2 spaces</option>
                    <option value={4}>4 spaces</option>
                    <option value={8}>8 spaces</option>
                  </select>
                </div>
                <button
                  onClick={minifyJson}
                  disabled={!isValid || !input.trim()}
                  className="px-4 py-1.5 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-sm"
                >
                  Minify JSON
                </button>
              </div>

              {/* Status */}
              {input && (
                <div className="mt-4">
                  {isValid !== null && (
                    <div className={`flex items-center gap-2 p-3 rounded-lg ${
                      isValid 
                        ? 'bg-green-50 border border-green-200' 
                        : 'bg-red-50 border border-red-200'
                    }`}>
                      {isValid ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="text-sm text-green-700 font-medium">Valid JSON ✓</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-4 w-4 text-red-500" />
                          <span className="text-sm text-red-700 font-medium">Invalid JSON</span>
                        </>
                      )}
                    </div>
                  )}

                  {error && (
                    <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-700 font-medium mb-1">Syntax Error:</p>
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Input Stats */}
              {input && (
                <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                  <span>Characters: {input.length.toLocaleString()}</span>
                  <span>Lines: {input.split('\n').length}</span>
                </div>
              )}
            </motion.div>
          </div>

          {/* Output Section */}
          <div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-5 sm:p-6 lg:p-8 hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2 text-green-500" />
                  Formatted JSON
                </h3>
                <div className="flex items-center space-x-2">
                  {output && (
                    <>
                      <button
                        onClick={copyToClipboard}
                        className="p-1.5 sm:p-2 text-gray-500 hover:text-[#8438ff] hover:bg-[#8438ff]/10 rounded-lg transition-all duration-200"
                        title="Copy to clipboard"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      <button
                        onClick={downloadJson}
                        className="p-1.5 sm:p-2 text-gray-500 hover:text-green-500 hover:bg-green-50 rounded-lg transition-all duration-200"
                        title="Download JSON"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="relative">
                <textarea
                  value={output}
                  readOnly
                  className="w-full h-64 sm:h-80 lg:h-96 p-3 sm:p-4 border-2 border-gray-200 rounded-lg sm:rounded-xl resize-none bg-gray-50 font-mono text-xs sm:text-sm focus:ring-2 focus:ring-[#8438ff]"
                  placeholder="Formatted JSON will appear here..."
                />
              </div>

              {output && (
                <div className="mt-3 sm:mt-4 flex items-center justify-between text-xs sm:text-sm text-gray-600">
                  <div className="flex items-center space-x-4">
                    <span>Characters: {output.length.toLocaleString()}</span>
                    <span>Lines: {output.split('\n').length}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-green-600 font-medium">Formatted</span>
                  </div>
                </div>
              )}

              {/* JSON Info Panel */}
              {!output && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">JSON Formatting Features</h4>
                  <ul className="text-xs text-blue-800 space-y-1">
                    <li>• Real-time syntax validation</li>
                    <li>• Customizable indentation (2, 4, or 8 spaces)</li>
                    <li>• Minify and beautify options</li>
                    <li>• Error highlighting and reporting</li>
                    <li>• One-click copy and download</li>
                  </ul>
                </div>
              )}
            </motion.div>
          </div>
        </div>

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
          className="mt-8"
        />
      </div>
    </div>
  );
};

export default JsonFormatter;
